import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';

import { CO2 } from '../../business-emissions/Components/CO2';
import {
  createParamsFromInput,
  getBusinessDefaultURL,
  getBusinessDefaultURLWithMerchantCode,
  getDashboardURLWithMerchantCode,
  getPaymentDefaultURL,
} from '../../util/getBusinessDefaultURL';
import { roundDecimals } from '../../util/roundNumber';
import { EstimationSubmit } from '../App';
import { OffsetColumn } from '../components/OffsetColumn';

import Plan1Image from '../images/Plan-1.png';
import Plan2Image from '../images/Plan-2.png';
import Plan3Image from '../images/Plan-3.png';

type Props = {
  currentEstimation: EstimationSubmit;
};

export const OffsetView = ({ currentEstimation }: Props): JSX.Element => {
  const [
    calculateEmissionsHrefBase,
    setCalculateEmissionsHrefBase,
  ] = useState<string>('');

  useEffect(() => {
    const params = createParamsFromInput(currentEstimation.input);
    const url = getPaymentDefaultURL() + '&' + params;

    setCalculateEmissionsHrefBase(url);
  }, [setCalculateEmissionsHrefBase]);

  const emissionQuantityKg = currentEstimation.emissionInTonnes * 1000;

  const columns: OffsetColumn[] = [
    {
      title: 'Climate starter',
      description:
        ' Offset 75% of the average emissions of a business like yours',
      image: {
        src: Plan1Image,
        alt: 'Climate starter visual',
      },
      emissionQuantityKg: emissionQuantityKg * 0.75,
      planURL: calculateEmissionsHrefBase,
    },
    {
      title: 'Climate friendly',
      description:
        ' Offset 100% of the average emissions of a business like yours',
      image: {
        src: Plan2Image,
        alt: 'Climate starter visual',
      },
      emissionQuantityKg: emissionQuantityKg * 1.00,
      planURL: calculateEmissionsHrefBase,
    },
    {
      title: 'Regenerative',
      description:
        ' Offset 125% of the average emissions of a business like yours',
      image: {
        src: Plan3Image,
        alt: 'Climate starter visual',
      },
      emissionQuantityKg: emissionQuantityKg * 1.25,
      planURL: calculateEmissionsHrefBase,
    },
  ];

  return (
    <Box textAlign="center">
      <Heading as="h1" mb="2">
        Offset your business emissions
      </Heading>
      <Text mb="10">
        Offsetting is a powerful tool that neutralises your carbon footprint
        with high quality carbon offset projects. Select a plan below to start
        making a difference.
      </Text>
      <Text mb="10">
        We estimated a business like yours produces{' '}
        <Text fontWeight={600} as="span">
          {roundDecimals(currentEstimation.emissionInTonnes)} tonnes
        </Text>{' '}
        of <CO2 /> per year.
      </Text>

      <Flex textAlign="left" mb="4" direction={{ base: 'column', md: 'row' }}>
        {columns.map((props, index) => {
          return (
            <OffsetColumn
              {...props}
              key={props.title}
              removeRightMargin={index === columns.length - 1}
            />
          );
        })}
      </Flex>

      <Text as="p">
        For a more accurate calculation of your overall business emissions try
        our{' '}
        <Button
          as="a"
          href={getBusinessDefaultURL()}
          colorScheme="black"
          variant="link"
          target="_blank"
        >
          business calculator
        </Button>{' '}
        or continue your climate journey in the{' '}
        <a href={getDashboardURLWithMerchantCode()} target="_blank">
          Climate Action Portal
        </a>
      </Text>
    </Box>
  );
};
